import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card';
import StatCard from '../../components/common/StatCard';
import DonutChart from '../../components/common/DonutChart';
import EmptyState from '../../components/common/EmptyState';
import LoadingRow from '../../components/common/LoadingRow';
import Avatar from '../../components/common/Avatar';
import LunchWeekWidget from './LunchWeekWidget';
import EventMiniRow from './EventMiniRow';
import RoomParentTodos from './RoomParentTodos';
import MemberInfoModal from '../roomparents/MemberInfoModal';
import AdminRoleLines, { hasAdminRole } from '../roomparents/AdminRoleLines';
import roomParentsService from '../../api/services/roomParents.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useEventsStore from '../../store/useEventsStore';
import useEmailStore from '../../store/useEmailStore';
import useFundraisingStore, { fundTotals } from '../../store/useFundraisingStore';
import useMyProfileStore, { selectMyMetadata } from '../../store/useMyProfileStore';
import { showToast } from '../../store/useToastStore';
import { fmt, todayISO } from '../../utils/formatters';
import { hoverTitle } from '../../utils/tooltip';
import { toChartEntries } from '../../utils/charts';
import { avatarFor } from '../../utils/avatar';
import { PATHS } from '../../routes/navConfig';

/* What an "Upcoming events" card shows: everything still to come, soonest
   first. When nothing is coming up the card falls back to the latest events
   that have already happened — a class with a calendar full of past events
   should see it, not an empty card. */
function upcomingOrRecent(list, today, max = 4) {
  const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
  const ahead = sorted.filter((e) => e.date >= today);
  return { ahead, rows: (ahead.length ? ahead : [...sorted].reverse()).slice(0, max) };
}

export default function DashboardPage() {
  const ctx = useContextStore(selectCurrentContext);
  const processScheduledSends = useEventsStore((s) => s.processScheduledSends);

  useEffect(() => {
    processScheduledSends();
  }, [processScheduledSends]);

  return ctx.type === 'master' ? <MasterDashboard ctx={ctx} /> : <ClassDashboard ctx={ctx} />;
}

const QUICK_ACTIONS = [
  { icon: '/assets/icons/calendar.svg', title: 'Add an event', sub: 'Schedule and notify', to: PATHS.CALENDAR, state: { openEventForm: true } },
  { icon: '/assets/icons/mail.svg', title: 'Compose email', sub: 'Draft from calendar', to: PATHS.EMAIL },
  { icon: '/assets/icons/donation.svg', title: 'Log a donation', sub: 'Keep funds current', to: PATHS.FUNDRAISING, state: { openDonationForm: true } },
  { icon: '/assets/icons/signup.svg', title: 'New sign-up', sub: 'Get volunteers for an event', to: PATHS.SIGNUPS, state: { view: 'create' } },
];

function ClassDashboard({ ctx }) {
  const { events, eventsNetworkId, loadEvents } = useEventsStore();
  const {
    donations, expenses, donationsNetworkId, expensesNetworkId, loadDonations, loadExpenses,
  } = useFundraisingStore();
  const { sentEmails, sentEmailsLoading, loadSentEmails } = useEmailStore();
  const meta = useMyProfileStore(selectMyMetadata);
  const firstName = (meta?.name || '').trim().split(/\s+/)[0];
  const navigate = useNavigate();

  const networkId = ctx?.id || null;

  /* This is the first screen after login, so it fetches everything it shows
     rather than waiting for another page to fill the stores: events, the funds
     ledger (cached per network) and the newest sent email. The to-do card pulls
     its own list. */
  useEffect(() => {
    if (!networkId) return;
    const fail = (err) => showToast(err?.message || "Couldn't load your dashboard.");
    loadEvents(networkId, { page: 1, limit: 100 }, 'class').catch(fail);
    loadDonations(networkId).catch(fail);
    loadExpenses(networkId).catch(fail);
    /* the "Last email" tile — newest first, so one row is enough */
    loadSentEmails(networkId, { page: 1, limit: 1 }).catch(fail);
  }, [networkId, loadEvents, loadDonations, loadExpenses, loadSentEmails]);

  const fundTotalsAll = fundTotals(donations, expenses);
  /* The ledger is cached per network — anything else means these totals aren't
     this class's (yet), so the tile says so instead of showing $0. */
  const fundsReady = donationsNetworkId === networkId && expensesNetworkId === networkId;
  const eventsPending = eventsNetworkId !== networkId;
  const today = todayISO();
  const classEvents = eventsPending ? [] : events.filter((e) => e.scope === 'class');
  /* `ahead` (today included) is what the tile counts; `upcoming` is what the
     card lists — the same events, or the most recent ones when none are left. */
  const { ahead, rows: upcoming } = upcomingOrRecent(classEvents, today);
  const lastEmail = [...sentEmails].sort((a, b) => b.sentDate.localeCompare(a.sentDate))[0];

  /* API stores the donation reason as a plain label string — group on it directly. */
  const donationsByReason = {};
  donations.forEach((d) => {
    const label = d.reason || 'Other';
    donationsByReason[label] = (donationsByReason[label] || 0) + Number(d.amount);
  });
  const expensesByTag = {};
  expenses.forEach((e) => {
    const label = e.category || 'Other';
    expensesByTag[label] = (expensesByTag[label] || 0) + Number(e.amount);
  });

  const lastEmailDate = lastEmail
    ? new Date(lastEmail.sentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';
  const lastEmailSub = lastEmail
    ? `${new Date(lastEmail.sentDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · ${lastEmail.subject}`
    : sentEmailsLoading
      ? 'loading…'
      : 'No emails yet';

  return (
    <div className="sec on" id="sec-dashboard">
      <div className="dashboard-heading">
        <div>
          <div className="eyebrow">Room parent workspace</div>
          <h2>{firstName ? `Good morning, ${firstName}.` : 'Good morning.'}</h2>
          <p>Everything for {ctx.name}, right where you need it.</p>
        </div>
      </div>

      <section className="quick-section" aria-labelledby="quick-title">
        <div className="dash-section-label">
          <h3 id="quick-title">Quick actions</h3>
          <span>Most-used tools</span>
        </div>
        <div className="quick-actions">
          {QUICK_ACTIONS.map((qa) => (
            <button className="quick-action" key={qa.title} onClick={() => navigate(qa.to, { state: qa.state })}>
              <img src={qa.icon} alt="" />
              <span className="qa-copy">
                <b>{qa.title}</b>
                <small>{qa.sub}</small>
              </span>
              <span className="arrow">›</span>
            </button>
          ))}
        </div>
      </section>

      <div className="dashboard-metrics">
        <div className="metric-card funds" title={hoverTitle('Open Fundraising')} onClick={() => navigate(PATHS.FUNDRAISING)}>
          <div className="metric-icon">
            <svg viewBox="0 0 24 24"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
          </div>
          <div>
            <div className="v">{fundsReady ? fmt(fundTotalsAll.balance) : '—'}</div>
            <div className="k">Available class funds</div>
            <div className="sub">
              {!fundsReady
                ? 'loading…'
                : fundTotalsAll.raised || fundTotalsAll.spent
                  ? `${fmt(fundTotalsAll.raised)} raised · ${fmt(fundTotalsAll.spent)} spent`
                  : 'nothing logged yet'}
            </div>
          </div>
        </div>
        <div className="metric-card" title={hoverTitle('Open Calendar')} onClick={() => navigate(PATHS.CALENDAR)}>
          <div className="metric-icon">
            <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
          </div>
          <div>
            <div className="v">{eventsPending ? '—' : ahead.length}</div>
            <div className="k">Upcoming events</div>
            <div className="sub">
              {eventsPending
                ? 'loading…'
                : ahead.length
                  ? `${ahead.length} event${ahead.length === 1 ? '' : 's'} still to come`
                  : classEvents.length
                    ? `${classEvents.length} event${classEvents.length === 1 ? '' : 's'}, all in the past`
                    : 'nothing scheduled'}
            </div>
          </div>
        </div>
        <div
          className="metric-card"
          title={hoverTitle('Open Previous Emails')}
          onClick={() => navigate(PATHS.EMAIL, { state: { pane: 'prev' } })}
        >
          <div className="metric-icon">
            <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
          </div>
          <div>
            <div className="v">{lastEmailDate}</div>
            <div className="k">Last email sent</div>
            <div className="sub" title={hoverTitle(lastEmailSub)}>{lastEmailSub}</div>
          </div>
        </div>
      </div>

      <div className="dash-grid">
        <RoomParentTodos networkId={networkId} />
        <Card
          title="Upcoming events"
          action={<span className="text-link" onClick={() => navigate(PATHS.CALENDAR)}>Open calendar</span>}
        >
          <div>
            {eventsPending ? (
              <LoadingRow>Loading events…</LoadingRow>
            ) : upcoming.length ? (
              upcoming.map((ev) => <EventMiniRow key={ev.id} event={ev} onClick={() => navigate(PATHS.CALENDAR)} />)
            ) : (
              <EmptyState>Nothing on the calendar yet.</EmptyState>
            )}
          </div>
        </Card>
      </div>

      <div className="dash-grid analytics">
        <Card
          title="Where donations came from"
          action={<span className="text-link" onClick={() => navigate(PATHS.FUNDRAISING)}>View ledger</span>}
        >
          {fundsReady ? (
            <DonutChart entries={toChartEntries(donationsByReason)} centerCaption="raised" />
          ) : (
            <LoadingRow>Loading donations…</LoadingRow>
          )}
        </Card>
        <Card
          title="Where class funds went"
          action={<span className="text-link" onClick={() => navigate(PATHS.FUNDRAISING)}>View expenses</span>}
        >
          {fundsReady ? (
            <DonutChart entries={toChartEntries(expensesByTag)} centerCaption="spent" />
          ) : (
            <LoadingRow>Loading expenses…</LoadingRow>
          )}
        </Card>
      </div>
    </div>
  );
}

function MasterDashboard({ ctx }) {
  const contexts = useContextStore((s) => s.contexts);
  const { events, eventsNetworkId, loadEvents } = useEventsStore();
  const [roomParents, setRoomParents] = useState(null);
  const [openMember, setOpenMember] = useState(null);
  const navigate = useNavigate();

  /* A master context's own id IS the school's network id. */
  const schoolId = ctx?.schoolId || ctx?.id || null;

  useEffect(() => {
    if (!schoolId) return;
    loadEvents(schoolId, { page: 1, limit: 100 }, 'master').catch((err) =>
      showToast(err?.message || "Couldn't load the master calendar.")
    );
  }, [schoolId, loadEvents]);

  /* Who has access, school-wide — admins across every class at this school. */
  useEffect(() => {
    setRoomParents(null);
    if (!schoolId) return undefined;
    let cancelled = false;
    roomParentsService
      .getSchoolRoomParents(schoolId)
      .then((rps) => { if (!cancelled) setRoomParents(rps); })
      .catch(() => { if (!cancelled) setRoomParents([]); });
    return () => { cancelled = true; };
  }, [schoolId]);

  const eventsPending = eventsNetworkId !== schoolId;
  const masterEvents = eventsPending ? [] : events.filter((e) => e.scope === 'master');
  /* the stat counts the whole master calendar; the card leads with what's still
     to come, and falls back to the most recent when that's nothing */
  const { rows: upcoming } = upcomingOrRecent(masterEvents, todayISO());
  /* The classes this room parent holds at this school. */
  const classCount = contexts.filter((c) => c.type === 'class' && c.schoolId === ctx.schoolId).length;
  const schoolName = ctx.schoolName || ctx.name;
  /* Room parents = admins of a class. Sub-group-only admins are listed too, but
     tagged and counted apart. Without the API's adminNetworkIds (no
     adminClasses on the rows) every admin counts, as before. */
  const groupOnlyCount = (roomParents || []).filter((rp) => rp.adminClasses && !rp.adminClasses.length).length;
  const rpCount = (roomParents || []).length - groupOnlyCount;

  return (
    <div className="sec on" id="sec-dashboard">
      <div className="dashboard-heading">
        <div>
          <div className="eyebrow">Room parent workspace</div>
          <h2>{ctx.name}</h2>
          <p>School-wide calendar, lunch menu, and access in one place.</p>
        </div>
      </div>

      <div className="stat-row">
        <StatCard
          label="Room Parents"
          value={roomParents ? rpCount : '—'}
          sub={
            !roomParents
              ? 'loading…'
              : groupOnlyCount
                ? `plus ${groupOnlyCount} group admin${groupOnlyCount === 1 ? '' : 's'}`
                : 'with access to this page'
          }
        />
        <StatCard
          label="School-wide Events"
          value={eventsPending ? '—' : masterEvents.length}
          sub={eventsPending ? 'loading…' : 'on the master calendar'}
        />
        <StatCard label="Classes" value={classCount} sub={`at ${schoolName}`} />
      </div>

      <div className="dash-grid">
        <Card
          title="Upcoming School-wide Events"
          action={<span className="btn btn-ghost btn-sm" onClick={() => navigate(PATHS.CALENDAR)}>View Calendar</span>}
        >
          <div>
            {eventsPending ? (
              <LoadingRow>Loading events…</LoadingRow>
            ) : upcoming.length ? (
              upcoming.map((ev) => <EventMiniRow key={ev.id} event={ev} onClick={() => navigate(PATHS.CALENDAR)} />)
            ) : (
              <EmptyState>Nothing on the master calendar yet.</EmptyState>
            )}
          </div>
        </Card>
        <Card
          title="This Week's Menu"
          action={<span className="btn btn-ghost btn-sm" onClick={() => navigate(PATHS.LUNCH)}>Edit</span>}
        >
          <LunchWeekWidget />
        </Card>
      </div>

      <Card
        title="Room Parents with Access"
        action={<span className="btn btn-ghost btn-sm" onClick={() => navigate(PATHS.ROOM_PARENTS)}>See Activity Log</span>}
      >
        <div>
          {!roomParents ? (
            <LoadingRow>Loading room parents…</LoadingRow>
          ) : roomParents.length ? (
            roomParents.map((rp) => {
              const av = avatarFor(rp.name, []);
              return (
                <div className="ev-row-mini" key={rp.id} onClick={() => setOpenMember(rp)}>
                  <Avatar src={rp.picture} initials={av.initials} color={av.color} size={32} fontSize={11} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ev-title">{rp.name}</div>
                    {hasAdminRole(rp) ? (
                      <AdminRoleLines member={rp} />
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{rp.group || rp.email || '—'}</div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState>No room parents yet.</EmptyState>
          )}
        </div>
      </Card>

      <MemberInfoModal member={openMember} onClose={() => setOpenMember(null)} />
    </div>
  );
}
